import type { StockQuantityInput } from '@/domains/command-center/handlers/stock/stockMode.types'
import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'
import type { DisplayUnit } from '@/utils/unit-util/UnitUtil.types'

import { MAX_QUANTITY_DIGITS, MODE_SIGNS, SIGN_MODES } from '@/domains/command-center/handlers/stock/stockMode.consts'
import { toBaseCount } from '@/utils/unit-util/unitUtil'

/** An optional sign, digits, then an optional fraction after a point or a comma. Nothing else is a quantity. */
const QUANTITY_PATTERN = /^([+\-−=])?(\d*)(?:[.,](\d*))?$/

/** Float noise from adding one to a decimal, so `0.1 + 1` types back as `1.1`. */
const INCREMENT_DECIMALS = 10

/**
 * Reads what the merchant typed in the quantity step.
 *
 * The number is in the variant's stock unit: `=4.82` is 4.82 kg on a kilogram variant. `inBaseUnits` turns it into
 * the whole base count the shelf holds.
 *
 * The sign picks the row, so `=48` means "set to 48" and `48` alone picks nothing. That is what keeps
 * a scanner safe here: it types digits only, so it can never pick a row by itself.
 */
const parseStockQuantity = (text: string | undefined): StockQuantityInput => {
  const match = QUANTITY_PATTERN.exec((text ?? '').trim())

  if (!match) {
    return { mode: null, quantity: null, isBarcodeLike: false, isMalformed: true, isNotWhole: false }
  }

  // The pattern admits only the four signs, so the capture is one of them.
  const [, sign, whole, fraction] = match as unknown as [
    string,
    keyof typeof SIGN_MODES | undefined,
    string,
    string | undefined
  ]
  // No quantity has more whole digits than the cap, fraction or not, so a longer run is a scanner typing.
  // Only the whole part counts: the digits after the point are a fraction of a unit.
  const isBarcodeLike = whole.length > MAX_QUANTITY_DIGITS
  const hasDigits = whole !== '' || Boolean(fraction)

  return {
    mode: sign ? SIGN_MODES[sign] : null,
    quantity: hasDigits && !isBarcodeLike ? Number(`${whole}.${fraction ?? ''}`) : null,
    isBarcodeLike,
    isMalformed: false,
    isNotWhole: false
  }
}

/**
 * The typed quantity as the whole base count the shelf holds, converted once.
 *
 * Before the catalogue loads the unit is undefined, so the number is already a base count. A figure that is not a
 * whole number of base units (`4.8205` kg) has no base count: the quantity is null and `isNotWhole` says why.
 */
const inBaseUnits = (input: StockQuantityInput, unit?: DisplayUnit): StockQuantityInput => {
  if (input.quantity === null) return input

  const quantity = toBaseCount({ quantity: input.quantity, unit })

  return { ...input, quantity, isNotWhole: quantity === null }
}

/** The text the box shows for a mode and a number. The reverse of `parseStockQuantity`. */
const formatStockQuantity = (mode: AdjustmentMode | null, quantity: number | null): string =>
  `${mode ? MODE_SIGNS[mode] : ''}${quantity ?? ''}`

/** One more of the same item was scanned, so the count goes up by one stock unit and the sign stays. */
const incrementStockQuantity = (text: string | undefined): string => {
  const { mode, quantity } = parseStockQuantity(text)

  return formatStockQuantity(mode, Number(((quantity ?? 0) + 1).toFixed(INCREMENT_DECIMALS)))
}

/**
 * The merchant's own text, with the barcode a scanner typed after it taken off.
 *
 * A scanner types into whatever has focus, so its code lands at the end of the box before the
 * palette hears about the scan. The code is known by then, so it is removed exactly.
 */
const withoutScannedCode = (text: string | undefined, code: string): string => {
  const value = text ?? ''

  return value.endsWith(code) ? value.slice(0, -code.length) : value
}

export { formatStockQuantity, inBaseUnits, incrementStockQuantity, parseStockQuantity, withoutScannedCode }
